import {cn} from '@sollapay/ui';
import {memo, useId} from 'react';

import {
  TrustAccountGroupedTable,
  TrustAccountStatusSections
} from '@/features/trust-accounts/components';

import {GuestAccountCard} from './GuestAccountCard';
import {GuestFirmHeader} from './GuestFirmHeader';
import {
  GUEST_ACCOUNT_CARDS_CLASS,
  GUEST_ACCOUNT_TABLE_PROPS,
  GUEST_FIRM_CARD_CLASS,
  GUEST_FIRM_PANEL_CLASS
} from '../config';

import type {GuestFirmGroup, GuestTrustAccountListItem} from '../utils';
import type {TrustAccountListColumn} from '@/features/trust-accounts/components';
import type {TrustAccountGroupFold, TrustStatusGroup} from '@/features/trust-accounts/utils';
import type {ListOnSortingChange, ListSortingState, ListView} from '@sollapay/ui/components';

type Row = GuestTrustAccountListItem;

export interface GuestFirmCardProps {
  group: GuestFirmGroup;
  statusGroups: TrustStatusGroup<Row>[];
  columns: TrustAccountListColumn<Row>[];
  view?: ListView;
  sorting?: ListSortingState;
  onSortingChange?: ListOnSortingChange;
  isCollapsed?: boolean;
  onToggle: (firmId: string) => void;
  groupFold: TrustAccountGroupFold;
}

/** Rows use `rowLink`, not `onRowClick`, which would nest the name link inside a `role="button"` row. */
export const GuestFirmCard = memo(function GuestFirmCard({
  group,
  statusGroups,
  columns,
  view = 'table',
  sorting,
  onSortingChange,
  isCollapsed = false,
  onToggle,
  groupFold
}: GuestFirmCardProps) {
  const id = useId();
  const headingId = `${id}-heading`;
  const panelId = `${id}-panel`;

  return (
    <section
      data-slot="guest-firm-card"
      aria-labelledby={headingId}
      className={GUEST_FIRM_CARD_CLASS}
    >
      <GuestFirmHeader
        firm={group.firm}
        accountCount={group.accounts.length}
        inviters={group.inviters}
        headingId={headingId}
        panelId={panelId}
        isCollapsed={isCollapsed}
        onToggle={() => onToggle(group.firm.id)}
      />
      <div id={panelId} className={cn(!isCollapsed && GUEST_FIRM_PANEL_CLASS)}>
        {!isCollapsed &&
          (view === 'card' ? (
            <TrustAccountStatusSections
              className={GUEST_ACCOUNT_CARDS_CLASS}
              groups={statusGroups}
              groupFold={groupFold}
              renderItem={renderAccountCard}
            />
          ) : (
            <TrustAccountGroupedTable
              {...GUEST_ACCOUNT_TABLE_PROPS}
              columns={columns}
              groups={statusGroups}
              groupFold={groupFold}
              sorting={sorting}
              onSortingChange={onSortingChange}
            />
          ))}
      </div>
    </section>
  );
});

const renderAccountCard = (item: Row) => <GuestAccountCard item={item} />;
